import React, { useCallback, useEffect, useMemo, useState } from 'react';
import type { AuthClient, AuthState } from '../client';
import { AuthContext, type AuthContextValue } from './auth-context';

interface AuthProviderProps {
  client: AuthClient;
  children: React.ReactNode;
}

export function AuthProvider({ client, children }: AuthProviderProps) {
  const [state, setState] = useState<AuthState>(client.getState());

  useEffect(() => {
    return client.onAuthStateChange(setState);
  }, [client]);

  const signInWithEmailAndPassword = useCallback(
    async (email: string, password: string) => {
      await client.signInWithEmailAndPassword(email, password);
    },
    [client],
  );

  const signUp = useCallback(
    async (email: string, password: string) => {
      await client.signUp(email, password);
    },
    [client],
  );

  const signInWithGoogle = useCallback(async () => {
    await client.signInWithGoogle();
  }, [client]);

  const signInWithApple = useCallback(async () => {
    await client.signInWithApple();
  }, [client]);

  const signInWithFacebook = useCallback(async () => {
    await client.signInWithFacebook();
  }, [client]);

  const logout = useCallback(async () => {
    await client.logout();
  }, [client]);

  const refresh = useCallback(async () => {
    await client.refresh();
  }, [client]);

  const getAuthHeaders = useCallback(() => {
    return client.getAuthHeaders();
  }, [client]);

  const value: AuthContextValue = useMemo(
    () => ({
      ...state,
      client,
      signInWithEmailAndPassword,
      signUp,
      signInWithGoogle,
      signInWithApple,
      signInWithFacebook,
      logout,
      refresh,
      getAuthHeaders,
    }),
    [
      state,
      client,
      signInWithEmailAndPassword,
      signUp,
      signInWithGoogle,
      signInWithApple,
      signInWithFacebook,
      logout,
      refresh,
      getAuthHeaders,
    ],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
